import getters from '@/study-search/store/getters';
import { MetaTags } from './MetaTags';
import { computed, Ref, ref } from 'vue';
import { PageTitleConstructor } from '@/study-search/models/page-title';
import { PaginationDefinition } from '@/study-search/models/definition';
import { Indexability } from '@/study-search/models/indexability/Indexability';

export class MetaInfo {
	private readonly pageTitleConstructor = new PageTitleConstructor();

	private isIndexable: Ref<boolean> = ref(false);
	private indexability = Indexability.getInstance();

	public async setIndexableFlag(): Promise<void> {
		const shouldIndex = await this.indexability.shouldIndex();
		this.isIndexable.value = shouldIndex;
	}

	/**
	 * Creates a URL from canonical and optionally sets or removes the page parameter
	 */
	private createUrlWithPageParameter(pageNumber?: number): string {
		const canonical = getters.getCanonical();
		const url = new URL(canonical);
		
		if (pageNumber) {
			url.searchParams.set('page', pageNumber.toString());
		} else {
			url.searchParams.delete('page');
		}
		
		return url.toString();
	}

	/**
	 * Checks if a next page should be shown based on current page and total results
	 */
	private shouldShowNextPage(currentPage: number, targetPage: number): boolean {
		const pageNumber = getters.getPageNumber();
		const totalSearchResults = getters.getTotalSearchResults();

		if (pageNumber === undefined) {
			return false;
		}

		const maxResultsForTargetPage = Number(PaginationDefinition.PAGE_SIZE) * targetPage;
		return pageNumber === currentPage && totalSearchResults > maxResultsForTargetPage;
	}

	public title = computed((): string => {
		return this.pageTitleConstructor.getMetaTitle();
	});

	public robots = computed((): string => {
		const pageNumber = getters.getPageNumber();
		
		if (pageNumber > 1) {
			return MetaTags.NOINDEX;
		}
		
		return this.isIndexable.value &&
			this.isSelfCanonical.value
			? MetaTags.INDEX
			: MetaTags.NOINDEX;
	});

	public description = computed((): string => {
		return this.pageTitleConstructor.getMetaDescription();
	});

	public canonical = computed((): string => {
		const pageNumber = getters.getPageNumber();
		
		if (pageNumber > 1) {
			return this.createUrlWithPageParameter();
		}
		
		return getters.getCanonical();
	});

	public showPrerenderStatusCode = computed((): boolean => {
		return this.robots.value !== MetaTags.INDEX.toString();
	});

	public showPrerenderHeader = computed((): boolean => {
		return !this.isSelfCanonical.value;
	});

	public showRelPrev = computed((): boolean => {
		const pageNumber: number = getters.getPageNumber();

		return pageNumber !== undefined && pageNumber === 2;
	});

	public relPrev = computed((): string => {
		return this.createUrlWithPageParameter();
	});

	public showRelNextPage2 = computed((): boolean => {
		return this.shouldShowNextPage(1, 2);
	});

	public relNextPage2 = computed((): string => {
		return this.createUrlWithPageParameter(2);
	});

	public showRelNextPage3 = computed((): boolean => {
		return this.shouldShowNextPage(2, 3);
	});

	public relNextPage3 = computed((): string => {
		return this.createUrlWithPageParameter(3);
	});

	/**
	 * Sets the Prerender header in accordance with the canonical URL, since the `prerender-header` meta tag acts as the canonical URL for Prerender.
	 */
	public prerenderHeader = computed((): string => {
		return `Location: ${this.canonical.value}`;
	});

	/**
	 * Sets the Prerender status code in accordance with the indexability policy and canonical URL.
	 */
	public prerenderStatusCode = computed((): number => {
		if (this.showPrerenderHeader.value) {
			return 301;
		}

		return 200;
	});

	/**
	 * Returns true if the canonical URL is the same as the current path (without the `bot` query parameter).
	 */
	private isSelfCanonical = computed((): boolean => {
		const url = new URL(window.location.href);
		url.searchParams.delete('bot');
		return this.canonical.value === url.toString();
	});
	
	/**
	 * Returns true if the current URL has a page query parameter.
	 */
	private hasPageParameter(): boolean {
		const url = new URL(window.location.href);
		return url.searchParams.has('page');
	}
}
